/**
 * Tracking spec v2 (Sep 2026) — session/attempt identity and the common
 * envelope stamped onto every event.
 *
 * Nothing here changes app behaviour: it only records identifiers and
 * timestamps so events can be joined (session → attempt) and so every event
 * carries the same cross-cutting dimensions without each call site repeating
 * them. See docs/analytics/tracking-events.csv for the catalogue.
 */
import { getUploadDimensions } from '../upload/uploadDimensions';
import { getDeviceLocationProps } from './deviceLocation';

export type EntrySource = 'qr' | 'sms_magic_link' | 'partner_token' | 'uwm_portal' | 'direct';
export type AttemptPath = 'picker' | 'camera' | 'recorder' | 'resume_same_session' | 'resume_cross_session';
export type CaptchaMode = 'enterprise' | 'checkbox_fallback' | 'disabled';
export type UserState = 'new_unverified' | 'existing';
export type AbandonStage =
  | 'preflight'
  | 'uploading'
  | 'paused'
  | 'identity'
  | 'business_confirm'
  | 'questions'
  | 'finalizing'
  | 'complete';

interface AttemptMeta {
  id: string;
  startedAt: number;
  path: AttemptPath;
  fileSize: number | null;
  /** attempt_id of the abandoned attempt this one resumes (cross-session). */
  resumesAttemptId: string | null;
  /** Cached landing-test speed handed to the engine as its seed, if any. */
  speedSeedMbps: number | null;
  /** Percent already on the server when the engine started (resumes). */
  startOffsetPct: number | null;
  captchaMode: CaptchaMode | null;
  userState: UserState | null;
  /** performance.now() of the last upload progress tick. */
  lastProgressAt: number | null;
  /** Seconds recorded in the in-page recorder, when the attempt came from it. */
  recordedDurationS: number | null;
  completed: boolean;
}

const SESSION_KEY = 'sv_session_id';

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const uuid = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/* ─── Session ─── */

let sessionId: string | null = null;
export const getSessionId = (): string => {
  if (sessionId) return sessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      sessionId = stored;
      return stored;
    }
    sessionId = uuid();
    sessionStorage.setItem(SESSION_KEY, sessionId);
    return sessionId;
  } catch {
    sessionId = uuid();
    return sessionId;
  }
};

/* ─── Arrival ─── */

let arrivalAt: number | null = null;
/** Called when the ticket screen first paints. Returns true only the first
 *  time per page load, so the caller can fire app_arrival exactly once even
 *  under React's dev double-mount. */
export const markArrival = (): boolean => {
  if (arrivalAt !== null) return false;
  arrivalAt = now();
  return true;
};
export const msSinceArrival = (): number | null => (arrivalAt === null ? null : Math.round(now() - arrivalAt));

/* ─── Once-per-page-load guards ───
   Module state resets with every page load, which is exactly the scope we
   want: one app_arrival, one human_signal, one ticket_ready per load, even
   when a screen mounts twice. (A sessionStorage flag was wrong here — it
   survived reloads in the same tab, so human_signal fired once per tab.) */

const onceFlags = new Set<string>();
export const oncePerPageLoad = (key: string): boolean => {
  if (onceFlags.has(key)) return false;
  onceFlags.add(key);
  return true;
};
export const hasHumanSignal = (): boolean => onceFlags.has('human_signal');
export const markHumanSignal = (): void => {
  onceFlags.add('human_signal');
};

/* ─── Page leave in progress (pagehide fired) ─── */
let leaving = false;
export const isLeaving = (): boolean => leaving;

/* ─── Recorder hand-off: the clip length is known before the attempt exists ─── */
let pendingRecordedDurationS: number | null = null;
export const setPendingRecordedDuration = (s: number | null): void => {
  pendingRecordedDurationS = s;
};
/** Why the recorder is mounting again: a retake or a camera-panel retry.
 *  recorder_open reports it so remounts don't inflate the recorder funnel. */
let recorderReopenReason: 'retake' | 'retry' | null = null;
export const markRecorderRetake = (): void => {
  recorderReopenReason = 'retake';
};
export const markRecorderRetry = (): void => {
  recorderReopenReason = 'retry';
};
export const consumeRecorderReopen = (): 'retake' | 'retry' | null => {
  const v = recorderReopenReason;
  recorderReopenReason = null;
  return v;
};

/* ─── Upload attempt ─── */

let attempt: AttemptMeta | null = null;

export const startAttempt = (
  path: AttemptPath,
  fileSize: number | null = null,
  opts: { resumesAttemptId?: string | null } = {},
): string => {
  attempt = {
    id: uuid(),
    startedAt: now(),
    path,
    fileSize,
    resumesAttemptId: opts.resumesAttemptId ?? null,
    speedSeedMbps: null,
    startOffsetPct: null,
    captchaMode: null,
    userState: null,
    lastProgressAt: null,
    recordedDurationS: path === 'recorder' ? pendingRecordedDurationS : null,
    completed: false,
  };
  pendingRecordedDurationS = null;
  return attempt.id;
};
export const getAttempt = (): Readonly<AttemptMeta> | null => attempt;
export const setAttemptMeta = (patch: Partial<Omit<AttemptMeta, 'id' | 'startedAt'>>): void => {
  if (attempt) Object.assign(attempt, patch);
};
export const noteUploadProgress = (): void => {
  if (attempt) attempt.lastProgressAt = now();
};
export const msSinceAttempt = (): number | null => (attempt ? Math.round(now() - attempt.startedAt) : null);
export const msSinceLastProgress = (): number | null =>
  attempt && attempt.lastProgressAt !== null ? Math.round(now() - attempt.lastProgressAt) : null;

/* ─── Entry source ─── */

export const getEntrySource = (): EntrySource => {
  if (typeof window === 'undefined') return 'direct';
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('partner-token')) return 'partner_token';
    if (params.get('associationId') || localStorage.getItem('associationId')) return 'uwm_portal';
    if (localStorage.getItem('loginWithMagicLink') === 'true') return 'sms_magic_link';
    if (params.get('qrcode') === 'true' || localStorage.getItem('qrcode') === 'true') return 'qr';
  } catch {
    /* ignore */
  }
  return 'direct';
};

export const getReferrerType = (): 'same_site' | 'cross_site' | 'none' => {
  if (typeof document === 'undefined' || !document.referrer) return 'none';
  try {
    const ref = new URL(document.referrer);
    const host = window.location.hostname;
    const root = host.split('.').slice(-2).join('.');
    return ref.hostname === host || ref.hostname.endsWith(`.${root}`) ? 'same_site' : 'cross_site';
  } catch {
    return 'cross_site';
  }
};

/* ─── Envelope ─── */

/* GA4 keeps at most 25 parameters per event and silently drops the rest after
   the request is sent. So the envelope is split:
   - per-event fields: only what changes during a session (attempt and timing),
     plus `online: false` / `page_visible: false` when they are not the norm;
   - session-level values go out once as GA4 user properties (see
     getUserProperties), which do not count against the per-event limit.
   Page URL, timestamp and screen size are collected by GA4 itself. */
export const getEnvelope = (): Record<string, string | number | boolean | null> => {
  const isBrowser = typeof window !== 'undefined';
  const env: Record<string, string | number | boolean | null> = {
    attempt_id: attempt?.id ?? null,
    t_since_arrival_ms: msSinceArrival(),
    t_since_attempt_ms: msSinceAttempt(),
  };
  if (isBrowser && !navigator.onLine) env.online = false;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') env.page_visible = false;
  return env;
};

/** Session-constant values, sent as GA4 user properties (36-char value cap). */
export const getUserProperties = (extra: Record<string, string | null | undefined> = {}): Record<string, string> => {
  const dims = getUploadDimensions();
  const isBrowser = typeof window !== 'undefined';
  const props: Record<string, string | null | undefined> = {
    sv_session_id: getSessionId(),
    entry_source: getEntrySource(),
    ua_family: dims.uaFamily,
    effective_type: dims.effectiveType,
    save_data: String(dims.saveData),
    viewport: isBrowser ? `${window.innerWidth}x${window.innerHeight}` : null,
    // Present only once the device location has been read (see deviceLocation.ts).
    ...getDeviceLocationProps(),
    ...extra,
  };
  const out: Record<string, string> = {};
  Object.entries(props).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') out[k] = String(v).slice(0, 36);
  });
  return out;
};

/* ─── Abandonment on page leave ───
   One pagehide listener for the whole app. Screens register a resolver that
   says which stage the user is in; the most recently registered resolver
   wins, and a screen unregisters on unmount. */

export type TelemetryExtra = Record<string, string | number | boolean | null | undefined>;
type StageResolver = () => { stage: AbandonStage; extra?: TelemetryExtra } | null;
const resolvers: StageResolver[] = [];
let leaveHandler: ((stage: AbandonStage, extra: TelemetryExtra) => void) | null = null;
let installed = false;

export const registerAbandonStage = (resolver: StageResolver): (() => void) => {
  resolvers.push(resolver);
  return () => {
    const i = resolvers.indexOf(resolver);
    if (i >= 0) resolvers.splice(i, 1);
  };
};

export const installLeaveWatch = (handler: (stage: AbandonStage, extra: TelemetryExtra) => void): void => {
  leaveHandler = handler;
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const onLeave = () => {
    leaving = true;
    for (let i = resolvers.length - 1; i >= 0; i -= 1) {
      const result = resolvers[i]();
      if (result) {
        leaveHandler?.(result.stage, result.extra ?? {});
        return;
      }
    }
  };
  window.addEventListener('pagehide', onLeave);
};
